<template>
  <div class="brand-list-view flex flex-col">
    <page-header :title="tr('Brands')">
      <template #buttons>
        <ui-button v-if="!isLoading" as-child>
          <router-link to="/brands/new">
            <plus-icon />
            {{ tr('Add new brand') }}
          </router-link>
        </ui-button>
      </template>
    </page-header>

    <system-level-notifications space-top />

    <empty-screen
      v-if="!isLoading && brands.length === 0"
      class="my-5"
      icon="magic-stars"
      :title="emptyScreenTitle"
      :description="emptyScreenDescription"
      :flag-text="emptyScreenFlagText"
      :path="emptyScreenPath"
    />

    <div
      v-else
      role="table"
      :aria-label="tr('Brands')"
      class="mt-5 flex flex-col gap-2 pb-5"
    >
      <div role="row" class="grid items-center gap-x-4 max-md:relative max-md:grid-cols-[auto_1fr] max-md:gap-y-3 md:grid-cols-[2.75rem_5rem_minmax(0,1fr)_7rem_2rem] px-4 text-xs font-semibold whitespace-nowrap text-muted-foreground max-md:hidden">
        <span role="columnheader"><span class="sr-only">{{ tr('Status') }}</span></span>
        <span role="columnheader">{{ tr('Profiles') }}</span>
        <span role="columnheader" :aria-sort="sortDirection === 'asc' ? 'ascending' : 'descending'">
          <button
            type="button"
            data-slot="sort-button"
            class="inline-flex cursor-pointer items-center gap-1 hover:text-foreground"
            @click="() => sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'"
          >
            {{ tr('Name') }}
            <arrow-up-icon v-if="sortDirection === 'asc'" class="size-3.5" />
            <arrow-down-icon v-else class="size-3.5" />
          </button>
        </span>
        <span role="columnheader" class="text-center">{{ tr('Automations') }}</span>
        <span role="columnheader" />
      </div>

      <template v-if="isLoading">
        <div
          v-for="i in 3"
          :key="'brand-skeleton-' + i"
          role="row"
          class="grid items-center gap-x-4 max-md:relative max-md:grid-cols-[auto_1fr] max-md:gap-y-3 md:grid-cols-[2.75rem_5rem_minmax(0,1fr)_7rem_2rem] glass-tile rounded-xl p-4 md:min-h-13 md:py-1.5"
        >
          <!-- The cells of a loaded row, with placeholders of the same size. -->
          <div role="cell" class="max-md:order-last max-md:col-span-2 max-md:border-t max-md:border-border max-md:pt-3">
            <!-- Built like the switch (track + thumb), so it sits on the same baseline. -->
            <span class="relative inline-flex items-center">
              <span data-slot="skeleton" class="inline-flex h-[1.15rem] w-8 animate-pulse items-center rounded-full border border-transparent bg-primary/10"><span class="block size-4" /></span>
            </span>
          </div>
          <div role="cell">
            <social-page only-icon />
          </div>
          <div role="cell" class="flex min-w-0 flex-col gap-0.5 max-md:pr-10">
            <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-48 max-w-full" /></span>
          </div>
          <div role="cell" class="flex h-5 items-center justify-center max-md:hidden">
            <ui-skeleton class="h-3.5 w-5" />
          </div>
          <div role="cell" class="max-md:absolute max-md:top-4 max-md:right-4">
            <ui-skeleton class="size-8 rounded-full" />
          </div>
        </div>
      </template>

      <div
        v-for="row in sortedBrands"
        v-else
        :key="row.id"
        role="row"
        class="grid items-center gap-x-4 max-md:relative max-md:grid-cols-[auto_1fr] max-md:gap-y-3 md:grid-cols-[2.75rem_5rem_minmax(0,1fr)_7rem_2rem] glass-tile rounded-xl p-4 md:min-h-13 md:py-1.5 transition-shadow duration-300 ease-in-out hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)]"
      >
        <div role="cell" class="max-md:order-last max-md:col-span-2 max-md:border-t max-md:border-border max-md:pt-3">
          <switcher
            v-tooltip="brandSwitchTooltip(row)"
            :model-value="!row.isInactive"
            :disabled="isBrandSwitchDisabled(row)"
            :loading="isBrandStatusLoading(row)"
            :aria-label="tr('Active')"
            @update:model-value="(val) => onChangeBrandStatus(row, val)"
          />
        </div>

        <div role="cell" :class="row.isInactive && 'opacity-50'">
          <social-page v-if="row.sourceProfiles.length" :source-profiles="row.sourceProfiles" only-icon />
          <span v-else class="text-sm text-muted-foreground">–</span>
        </div>

        <div role="cell" :class="[ 'flex min-w-0 flex-col gap-0.5 max-md:pr-10', row.isInactive && 'opacity-50' ]">
          <router-link :to="'/brands/' + row.id" class="line-clamp-2 text-sm font-medium text-foreground no-underline hover:text-primary">
            {{ row.name || '–' }}
          </router-link>
          <div v-if="brandErrors(row).length" class="flex flex-wrap gap-1.5">
            <ui-badge
              v-for="(error, idx) in brandErrors(row)"
              :key="'status-' + row.id + '-' + idx"
              :variant="badgeVariant(error.type)"
              class="max-w-full truncate"
            >
              {{ error.label }}
            </ui-badge>
          </div>
        </div>

        <div role="cell" :class="[ 'text-center text-sm tabular-nums max-md:hidden', row.isInactive && 'opacity-50' ]">
          {{ row.rules_count ? row.rules_count : '–' }}
        </div>

        <div role="cell" class="max-md:absolute max-md:top-4 max-md:right-4">
          <arrow-link :to="'/brands/' + row.id" :label="row.name || tr('Brand')" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ArrowDown, ArrowUp, Plus } from '@lucide/vue';

import AssetStatusMixin from '@/mixins/AssetStatus.js';
import BrandActivationMixin from '@/mixins/BrandActivation.js';
import ArrowLink from '@/ui/components/ArrowLink.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import Switcher from '@/ui/components/Switcher.vue';
import SystemLevelNotifications from '@/ui/components/SystemLevelNotifications.vue';

export default {
  name: 'BrandListView',
  components: {
    ArrowDownIcon: ArrowDown,
    ArrowLink,
    ArrowUpIcon: ArrowUp,
    EmptyScreen,
    PlusIcon: Plus,
    SocialPage,
    PageHeader,
    Switcher,
    SystemLevelNotifications,
  },
  mixins: [ AssetStatusMixin, BrandActivationMixin ],
  data: () => ({
    searchTerm: '',
    sortDirection: 'asc',
  }),
  computed: {
    allBrands() {
      return this.$root.currentCompany?.brands ?? [];
    },
    brands() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) {
        return this.allBrands;
      }
      return this.allBrands.filter(b => (b.name || '').toLowerCase().includes(term));
    },
    sortedBrands() {
      const direction = this.sortDirection === 'asc' ? 1 : -1;

      return [ ...this.brands ].sort((a, b) => direction * (a.name || '').localeCompare(b.name || '', this.$root.language));
    },
    isLoading() {
      return !this.$root.currentCompany;
    },
    hasActiveSearch() {
      return this.searchTerm.trim().length > 0;
    },
    emptyScreenTitle() {
      return this.hasActiveSearch
        ? this.tr('No brands found for your search')
        : this.tr('No brands yet');
    },
    emptyScreenDescription() {
      return this.hasActiveSearch
        ? this.tr('Try a different search term.')
        : this.tr('Create your first brand to start organizing your ad accounts and content sources.');
    },
    emptyScreenFlagText() {
      return this.hasActiveSearch ? this.tr('No brands found') : this.tr('Create brand');
    },
    emptyScreenPath() {
      return this.hasActiveSearch ? '' : '/brands/new';
    },
  },
  methods: {
    badgeVariant(type) {
      if (type === 'error') {
        return 'destructive';
      }

      return type === 'warning' ? 'warning' : 'secondary';
    },
    brandErrors(row) {
      const brandStatus = this.brandStatusText(row);
      if (brandStatus) {
        return [ brandStatus ];
      }

      return [ this.adAccountError(row), this.sourceProfileError(row) ].filter(Boolean);
    },
    sourceProfileError(row) {
      for (const profile of row.sourceProfiles) {
        const status = this.sourceProfileStatusText(profile);
        if (status) {
          return status;
        }
      }
      return null;
    },
    adAccountError(row) {
      const aa = row.meta_ad_account;

      return this.adAccountStatusText(aa);
    },
  },
};
</script>
